/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import ShadowMapBlurShader from "App/render/webgl/shaders/shadow_map_variance/shadow_map_blur";
import View from "App/view/view";
import {Containers, Factory} from "App/utils/factory";
import {Framebuffer} from "App/utils/frame_buffer";
import {vec3} from "gl-matrix";
import IScene from "js_interface/src/scene_interface";

export default
class ShadowMapBlur extends Pass {

    public static TypeName: string = "ShadowMapBlur";
    private _shader: ShadowMapBlurShader;
    private _quad: WebGLBuffer;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._shader = null;
        this._quad = null;

        this.initialize();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ShadowMapBlur {
        return new ShadowMapBlur(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        let gl = this._context.getWebGLRenderingContext();

        // Get shader
        let shaders = this._renderGraph.getLayer('shadowMaps').getShaders();
        this._shader = shaders.get(ShadowMapBlurShader.TypeName);

        // Create quad
        let vertices = [
            -1, 1, 0.0, 0.0, 1.0,
            -1, -1, 0.0, 0.0, 0.0,
            1, 1, 0.0, 1.0, 1.0,
            1, -1, 0.0, 1.0, 0.0
        ];

        this._quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quad);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }

    public release() {
        let gl = this._context.getWebGLRenderingContext();
        gl.deleteBuffer(this._quad);

        // Call parent to clear
        super.release();
    }

    public render(aView: View, aOutput: Framebuffer, aTexture: WebGLTexture, direction: vec3) {
        /* jshint camelcase:false */
        this.view = aView;

        this.state.resetRenderNodesFilters();

        let gl = this._context.getWebGLRenderingContext();
        let textureSize = this._renderGraph.getLayer('shadowMaps').getTextureSize();

        gl.viewport(0, 0, textureSize, textureSize);
        gl.scissor(0, 0, textureSize, textureSize);

        aOutput.bind();
        gl.disable(gl.DEPTH_TEST);

        // Set shader index (0 - 32bit, 1 - 16bit)
        let index = 0;
        if (this._sceneAPI.isMobile()) {
            index = 1;
        }

        // bind shader
        this._activeShader = this._shader;
        this._activeShaderRenderable = this._activeShader.get(index);
        this._activeShaderRenderable.render();

        this.state.setRenderablesFilters(this._activeShaderRenderable.getRenderablesFilters());

        // Render quad
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quad);

        let uniformLocations = this._activeShaderRenderable.getUniformLocations();

        // Textures
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, aTexture);
        gl.uniform1i(uniformLocations.u_texture, 0);

        // Uniforms
        gl.uniform2f(uniformLocations.u_textureSize, textureSize, textureSize);
        gl.uniform2fv(uniformLocations.u_direction, direction);

        // Attributes
        let attributeLocations = this._activeShaderRenderable.getAtttributesLocations();
        this._activeShaderRenderable.enableAttributes();
        gl.vertexAttribPointer(attributeLocations.a_position, 3, gl.FLOAT, false, 5 * 4, 0);
        gl.vertexAttribPointer(attributeLocations.a_texcoords, 2, gl.FLOAT, false, 5 * 4, 3 * 4);

        // Draw and release
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        this._activeShaderRenderable.finishRender();

        // Reset
        this.view = null;
        gl.enable(gl.DEPTH_TEST);
    }
}

Factory.register(Containers.PASSES_SM, ShadowMapBlur);
